export enum SubscriptionStatus {
  TRIALING = 'TRIALING',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  CANCELED = 'CANCELED',
  /**
   * A renewal/upgrade charge failed (P4). Capability + feature resolution treats
   * this as the **ACTIVE** set (`ResourceIdentifierService
   * .normalizeStatusForCapabilities`, alongside `TRIALING -> ACTIVE`): full
   * access is retained through the 7-day dunning grace, consistent with warmup,
   * which also keeps running (the payment-failed handler fires no plan-downgrade
   * cascade). Nothing tightens at the failure instant -- the grace is baked into
   * the subscription's `expiresAt`, and access is removed only when the daily
   * sweep downgrades the subscription to `free` after it lapses.
   */
  PAST_DUE = 'PAST_DUE',
}

/**
 * A plan's recurring billing cadence — the SINGLE source of truth for the cycle
 * set, shared by `apps/api`, `apps/admin`, `apps/web`, and the `plans.billingCycle`
 * DB enum. Previously duplicated as three independent union types that could drift
 * from each other and from the column.
 *
 * Three values only: semi-annual was evaluated and dropped (2026-07-27) — it would
 * have needed 6 new tier prices plus 6 Paddle price objects in staging AND prod,
 * and the code plumbing was the cheap part. Adding a value here means an
 * `ALTER TABLE plans MODIFY billingCycle ENUM(...)` in the same change.
 */
export const BILLING_CYCLES = ['monthly', 'quarterly', 'yearly'] as const;

export type BillingCycle = (typeof BILLING_CYCLES)[number];

/** Cycle → plan duration in days. Mirrors the `plans.validityInDays` the seed writes. */
export const BILLING_CYCLE_TO_DAYS: Record<BillingCycle, number> = {
  monthly: 30,
  quarterly: 90,
  yearly: 365,
};

/** Inverse of `BILLING_CYCLE_TO_DAYS`: 30→monthly, 90→quarterly, 365→yearly; anything else → null. */
export function billingCycleFromDays(validityInDays: number | null): BillingCycle | null {
  const match = (Object.entries(BILLING_CYCLE_TO_DAYS) as Array<[BillingCycle, number]>).find(
    ([, days]) => days === validityInDays,
  );
  return match ? match[0] : null;
}

/**
 * Who owns the org's plan changes — `billingManagedBy` on `GET /billing/subscription`.
 * `SALESHANDY` may not change plan at all (040007); `INTERNAL` (APUS-assigned)
 * may only check out a strictly higher-priced plan (040008).
 */
export const BILLING_MANAGED_BY = {
  SELF_SERVE: 'SELF_SERVE',
  SALESHANDY: 'SALESHANDY',
  INTERNAL: 'INTERNAL',
} as const;

export type BillingManagedBy = (typeof BILLING_MANAGED_BY)[keyof typeof BILLING_MANAGED_BY];

export enum WebhookEventStatus {
  PENDING = 'PENDING',
  PROCESSED = 'PROCESSED',
  FAILED = 'FAILED',
}
